// Sidebar header station picker for the publisher admin pages. Replaces
// the static {config.name} display in PublisherAdminLayout with a
// dropdown that lets multi-station staff jump between stations without
// returning to /admin.
//
// Renders the plain station name when the signed-in user only has one
// station — no point in showing a chevron that opens a single-item list.

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Check, LayoutGrid } from 'lucide-react';
import { useAuth } from '../auth/useAuth';

interface Props {
  currentSlug: string;
  currentName: string;
}

const StationSwitcher: React.FC<Props> = ({ currentSlug, currentName }) => {
  const { myAdminStations } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const stations = myAdminStations ?? [];

  // Solo (or unknown — pre-fetch state) → render the plain name.
  if (stations.length <= 1) {
    return <div className="text-base font-semibold mt-1 truncate">{currentName}</div>;
  }

  // Swap the slug segment in the current pathname so
  // /admin/<old>/foo → /admin/<new>/foo. Preserves nested admin sub-routes
  // when switching. The /admin prefix is preserved.
  const goToStation = (slug: string) => {
    const rest = location.pathname.replace(/^\/admin\/[^/]+/, '');
    navigate(`/admin/${slug}${rest || ''}`);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 mt-1 px-2 py-1.5 -mx-2 rounded-md hover:bg-slate-800 text-left"
      >
        <span className="text-base font-semibold truncate">{currentName}</span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white text-slate-900 rounded-md shadow-xl border border-slate-200 overflow-hidden z-20 max-h-[60vh] overflow-y-auto">
          {stations.map((s) => {
            const isCurrent = s.slug === currentSlug;
            const label = s.displayName || s.name;
            return (
              <button
                key={s.slug ?? s.name}
                type="button"
                disabled={!s.slug}
                onClick={() => s.slug && goToStation(s.slug)}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed ${
                  isCurrent ? 'bg-slate-50 font-medium' : ''
                }`}
              >
                <span className="truncate">{label}</span>
                {isCurrent && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/admin');
            }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-slate-600 hover:bg-slate-50 border-t border-slate-100"
          >
            <LayoutGrid className="w-4 h-4" />
            View all stations
          </button>
        </div>
      )}
    </div>
  );
};

export default StationSwitcher;
